/**
 * PromptVersionSidebar
 *
 * Right sidebar showing version history for a prompt key.
 * Reuses shared modal components for deploy/rollback/delete.
 */

import React, { useState } from 'react';
import { Rocket, Pencil, Trash2, RotateCcw, GitCompare } from 'lucide-react';
import { Button } from '../../../../ui/button';
import { ScrollArea } from '../../../../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../ui/tooltip';
import VersionBadge from '../../shared/VersionBadge';
import DeployConfirmModal from '../../shared/DeployConfirmModal';
import RollbackModal from '../../shared/RollbackModal';
import DeleteConfirmModal from '../../shared/DeleteConfirmModal';
import CreateDraftModal from '../../shared/CreateDraftModal';
import PromptDiffModal from './PromptDiffModal';
import { highestVersion } from '../../utils/versionUtils';
import {
  usePromptVersions,
  useCreatePromptDraft,
  useDeployPrompt,
  useRollbackPrompt,
  useDeletePromptDraft,
} from '../../hooks/usePrompts';
import type { PromptVersionSummary } from '../../types/prompt';

interface PromptVersionSidebarProps {
  promptKey: string;
  taxonomyKey: string;
  onEditDraft?: (versionId: string) => void;
  selectedVersionId?: string | null;
  onSelectVersion?: (versionId: string) => void;
}

const PromptVersionSidebar: React.FC<PromptVersionSidebarProps> = ({
  promptKey,
  taxonomyKey,
  onEditDraft,
  selectedVersionId,
  onSelectVersion,
}) => {
  const { data: versions, isLoading } = usePromptVersions(promptKey, taxonomyKey);
  const createDraft = useCreatePromptDraft();
  const deployPrompt = useDeployPrompt();
  const rollbackPrompt = useRollbackPrompt();
  const deleteDraft = useDeletePromptDraft();

  const [deployModalVersion, setDeployModalVersion] = useState<PromptVersionSummary | null>(null);
  const [rollbackModalVersion, setRollbackModalVersion] = useState<PromptVersionSummary | null>(null);
  const [deleteModalVersion, setDeleteModalVersion] = useState<PromptVersionSummary | null>(null);
  const [createDraftOpen, setCreateDraftOpen] = useState(false);
  const [diffVersion, setDiffVersion] = useState<PromptVersionSummary | null>(null);

  const hasDraft = versions?.some((v) => v.status === 'draft');
  const liveVersion = versions?.find((v) => v.status === 'live');

  // Pre-compute values for CreateDraftModal (avoids IIFE in JSX)
  const allVersionNums = versions?.map((v) => v.versionNumber).filter(Boolean) as string[] ?? [];
  const draftHighestVersion = highestVersion(
    allVersionNums.length > 0 ? allVersionNums : (liveVersion ? [liveVersion.versionNumber] : ['0.0'])
  );
  const draftLatestVersion = versions?.find((v) => v.versionNumber === draftHighestVersion);

  const handleDeploy = (note: string) => {
    if (deployModalVersion) {
      deployPrompt.mutate(
        { versionId: deployModalVersion.id, deploymentNote: note },
        { onSuccess: () => setDeployModalVersion(null) }
      );
    }
  };

  const handleRollback = (note: string) => {
    if (rollbackModalVersion) {
      rollbackPrompt.mutate(
        { versionId: rollbackModalVersion.id, note },
        { onSuccess: () => setRollbackModalVersion(null) }
      );
    }
  };

  const handleDelete = () => {
    if (deleteModalVersion) {
      deleteDraft.mutate(deleteModalVersion.id, {
        onSuccess: () => {
          setDeleteModalVersion(null);
          if (liveVersion) onSelectVersion?.(liveVersion.id);
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-[340px] flex-shrink-0">
        <div className="rounded-lg border bg-white p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-[340px] flex-shrink-0">
        <div className="rounded-lg border bg-white py-4">
          <div className="flex items-center justify-between mb-3 px-4">
            <h3 className="font-semibold text-sm">Version History</h3>
            {!hasDraft && versions && versions.length > 0 && (
              <Button
                variant="toolbar"
                size="sm"
                onClick={() => setCreateDraftOpen(true)}
                disabled={createDraft.isPending}
              >
                Create Draft
              </Button>
            )}
          </div>

          <ScrollArea className="h-[calc(100vh-320px)] px-4">
            <div className="space-y-2">
              {versions?.map((v) => (
                <div
                  key={v.id}
                  className={`border rounded-md p-3 cursor-pointer transition-colors flex flex-col gap-1.5 ${
                    selectedVersionId === v.id
                      ? 'border-blue-300 bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onSelectVersion?.(v.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">v{v.versionNumber}</span>
                    <VersionBadge status={v.status} />
                  </div>
                  {/* Only show the Lines row when there are actual changes.
                     `null` = not computed (live/archived); `0` = computed
                     and identical to parent. Both cases hide the row. */}
                  <div className={`flex items-center gap-1.5 ${!v.linesAdded && !v.linesRemoved ? 'invisible' : ''}`}>
                    <span className="text-xs text-gray-400">Lines:</span>
                    {v.linesAdded ? (
                      <span className="inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                        +{v.linesAdded} added
                      </span>
                    ) : null}
                    {v.linesRemoved ? (
                      <span className="inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                        -{v.linesRemoved} removed
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between min-h-[28px]">
                    <p className="text-xs text-gray-500 truncate mr-1">
                      {new Date(v.createdAt).toLocaleDateString()}
                      {v.deployedAt && ` · Deployed ${new Date(v.deployedAt).toLocaleDateString()}`}
                    </p>

                    <TooltipProvider delayDuration={300}>
                      <div className="flex gap-0.5">
                        {v.status === 'draft' && (
                        <>
                          {onEditDraft && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Edit draft" onClick={(e) => { e.stopPropagation(); onEditDraft(v.id); }}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit draft</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Deploy to live" onClick={(e) => { e.stopPropagation(); setDeployModalVersion(v); }}>
                                <Rocket className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Deploy to live</TooltipContent>
                          </Tooltip>
                          {liveVersion && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Compare with live" onClick={(e) => { e.stopPropagation(); setDiffVersion(v); }}>
                                  <GitCompare className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Compare with live</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700" aria-label="Delete draft" onClick={(e) => { e.stopPropagation(); setDeleteModalVersion(v); }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete draft</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                      {v.status === 'archived' && (
                        <>
                          {liveVersion && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Compare with live" onClick={(e) => { e.stopPropagation(); setDiffVersion(v); }}>
                                  <GitCompare className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Compare with live</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7" aria-label="Rollback to this version" onClick={(e) => { e.stopPropagation(); setRollbackModalVersion(v); }}>
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rollback to this version</TooltipContent>
                          </Tooltip>
                        </>
                      )}
                      </div>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {(!versions || versions.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-4">No versions yet.</p>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {deployModalVersion && (
        <DeployConfirmModal
          open={!!deployModalVersion}
          onOpenChange={(open) => !open && setDeployModalVersion(null)}
          versionNumber={deployModalVersion.versionNumber}
          type="prompt"
          onConfirm={handleDeploy}
          isDeploying={deployPrompt.isPending}
        />
      )}
      {rollbackModalVersion && (
        <RollbackModal
          open={!!rollbackModalVersion}
          onOpenChange={(open) => !open && setRollbackModalVersion(null)}
          versionNumber={rollbackModalVersion.versionNumber}
          type="prompt"
          onConfirm={handleRollback}
          isRollingBack={rollbackPrompt.isPending}
        />
      )}
      {deleteModalVersion && (
        <DeleteConfirmModal
          open={!!deleteModalVersion}
          onOpenChange={(open) => !open && setDeleteModalVersion(null)}
          versionNumber={deleteModalVersion.versionNumber}
          type="prompt"
          onConfirm={handleDelete}
          isDeleting={deleteDraft.isPending}
        />
      )}
      {diffVersion && liveVersion && (
        <PromptDiffModal
          open={!!diffVersion}
          onOpenChange={(open) => !open && setDiffVersion(null)}
          versionAId={liveVersion.id}
          versionBId={diffVersion.id}
          versionALabel={`v${liveVersion.versionNumber}`}
          versionBLabel={`v${diffVersion.versionNumber}`}
        />
      )}
      {createDraftOpen && (
        <CreateDraftModal
          open={createDraftOpen}
          onOpenChange={setCreateDraftOpen}
          currentVersion={draftHighestVersion}
          type="prompt"
          onConfirm={(versionNumber, sourceVersionId) => {
            createDraft.mutate({ promptKey, options: { versionNumber, sourceVersionId, taxonomyKey } }, {
              onSuccess: (data) => {
                setCreateDraftOpen(false);
                onSelectVersion?.(data.id);
              },
            });
          }}
          isCreating={createDraft.isPending}
          liveSource={liveVersion ? { id: liveVersion.id, versionNumber: liveVersion.versionNumber } : undefined}
          latestSource={draftLatestVersion ? { id: draftLatestVersion.id, versionNumber: draftLatestVersion.versionNumber } : undefined}
        />
      )}
    </>
  );
};

export default PromptVersionSidebar;
